<template>
  <v-card
    flat
    class="module-sidebar"
    :style="{
      background: theme.cardBodyBg,
      color: theme.cardBodyColor
    }"
  >
    <!-- ===================================================== -->
    <!-- SEARCH -->
    <!-- ===================================================== -->
    <div class="sidebar-search-wrapper">
      <v-text-field
        v-model="search"
        :label="t('search')"
        variant="outlined"
        density="compact"
        clearable
        hide-details
        prepend-inner-icon="mdi-magnify"
        class="sidebar-search"
        :style="
          sysTypo.sectionTypography.input
        "
      />
    </div>

    <!-- ===================================================== -->
    <!-- MENU -->
    <!-- ===================================================== -->
    <div class="sidebar-menu">
      <template
        v-for="item in filteredMenu"
        :key="item.id"
      >
        <!-- ================================================= -->
        <!-- GROUP -->
        <!-- ================================================= -->
        <div
          v-if="item.children"
          class="sidebar-group"
        >
          <button
            type="button"
            class="sidebar-group-header"
            :class="{
              active: isGroupActive(item)
            }"
            @click="toggleGroup(item.id)"
          >
            <v-icon
              size="21"
              class="sidebar-icon"
            >
              {{ item.icon }}
            </v-icon>

            <span
              class="sidebar-label"
              :style="
                sysTypo.sectionTypography.body
              "
            >
              {{ item.title }}
            </span>

            <v-icon
              size="18"
              class="group-chevron"
            >
              {{
                isGroupOpen(item.id)
                  ? 'mdi-chevron-up'
                  : 'mdi-chevron-down'
              }}
            </v-icon>
          </button>

          <!-- ================================================= -->
          <!-- CHILDREN -->
          <!-- ================================================= -->
          <div
            v-show="isGroupOpen(item.id)"
            class="sidebar-group-children"
          >
            <template
              v-for="child in item.children"
              :key="child.id"
            >
              <!-- ============================================= -->
              <!-- SUB-GROUP -->
              <!-- ============================================= -->
              <div
                v-if="child.children"
                class="sidebar-subgroup"
              >
                <button
                  type="button"
                  class="sidebar-child-item subgroup-header"
                  :class="{
                    active: isGroupActive(child)
                  }"
                  @click="toggleGroup(child.id)"
                >
                  <v-icon
                    size="18"
                    class="sidebar-child-icon"
                  >
                    {{
                      child.icon ||
                      'mdi-circle-small'
                    }}
                  </v-icon>

                  <span
                    class="sidebar-child-label"
                    :style="
                      sysTypo.sectionTypography.small
                    "
                  >
                    {{ child.title }}
                  </span>

                  <v-icon
                    size="16"
                    class="group-chevron"
                  >
                    {{
                      isGroupOpen(child.id)
                        ? 'mdi-chevron-up'
                        : 'mdi-chevron-down'
                    }}
                  </v-icon>
                </button>

                <div
                  v-show="isGroupOpen(child.id)"
                  class="sidebar-subgroup-children"
                >
                  <button
                    v-for="sub in child.children"
                    :key="sub.id"
                    type="button"
                    class="sidebar-child-item sidebar-sub-item"
                    :class="{
                      active: isActive(sub)
                    }"
                    @click="navigate(sub)"
                  >
                    <v-icon
                      size="17"
                      class="sidebar-child-icon"
                    >
                      {{
                        sub.icon ||
                        'mdi-circle-small'
                      }}
                    </v-icon>

                    <span
                      class="sidebar-child-label"
                      :style="
                        sysTypo.sectionTypography.small
                      "
                    >
                      {{ sub.title }}
                    </span>
                  </button>
                </div>
              </div>

              <!-- ============================================= -->
              <!-- NORMAL CHILD -->
              <!-- ============================================= -->
              <button
                v-else
                type="button"
                class="sidebar-child-item"
                :class="{
                  active: isActive(child)
                }"
                @click="navigate(child)"
              >
                <v-icon
                  size="18"
                  class="sidebar-child-icon"
                >
                  {{
                    child.icon ||
                    'mdi-circle-small'
                  }}
                </v-icon>

                <span
                  class="sidebar-child-label"
                  :style="
                    sysTypo.sectionTypography.small
                  "
                >
                  {{ child.title }}
                </span>
              </button>
            </template>
          </div>
        </div>

        <!-- ================================================= -->
        <!-- ROOT ITEM -->
        <!-- ================================================= -->
        <button
          v-else
          type="button"
          class="sidebar-item"
          :class="{
            active: isActive(item)
          }"
          @click="navigate(item)"
        >
          <v-icon
            size="21"
            class="sidebar-icon"
          >
            {{ item.icon }}
          </v-icon>

          <span
            class="sidebar-label"
            :style="
              sysTypo.sectionTypography.body
            "
          >
            {{ item.title }}
          </span>

          <span
            v-if="item.badge"
            class="sidebar-badge"
          >
            {{ item.badge }}
          </span>
        </button>
      </template>
    </div>
  </v-card>
</template>

<script setup>
import {
  computed,
  ref,
  watch
} from 'vue'

import {
  useRoute,
  useRouter
} from 'vue-router'

import {
  useI18n
} from 'vue-i18n'

import {
  useLangStore
} from '@/store/lang'

import {
  useAuthStore,
  useThemeStore
} from '@/store/auth'

import {
  useSysTypo
} from '@/store/useSysTypo'

// ============================================================
// STORES / ROUTER
// ============================================================

const auth =
  useAuthStore()

const themeStore =
  useThemeStore()

const sysTypo =
  useSysTypo()

const lang =
  useLangStore()

const route =
  useRoute()

const router =
  useRouter()

const {
  t,
  locale
} = useI18n()

// ============================================================
// THEME / LANGUAGE
// ============================================================

const theme =
  computed(
    () =>
      themeStore.sectionTheme ||
      {}
  )

const currentLanguage =
  computed(
    () =>
      lang.currentLanguage ||
      'en'
  )

watch(
  currentLanguage,
  newLanguage => {
    locale.value =
      newLanguage
  },
  {
    immediate: true
  }
)

// ============================================================
// SEARCH
// ============================================================

const search =
  ref('')

// ============================================================
// OPEN GROUPS
// ============================================================

const open =
  ref([])

// ============================================================
// MENU
// ============================================================

const menu =
  computed(() => [
    // ========================================================
    // ADMINISTRATION
    // ========================================================
    {
      id:
        'admin-section',

              title:
                currentLanguage.value === 'en'
                  ? 'Administration'
                  : 'Administration',

              icon:
                'mdi-shield-account-outline',

              children: [
                {
                  id:
                    'admin-assessment',

                  title:
                    currentLanguage.value === 'en'
                      ? 'Take assessment'
                      : 'Faire une évaluation',

                  icon:
                    'mdi-clipboard-text-outline',

                  to:
                    router.resolve({
                      name:
                        currentLanguage.value === 'en'
                          ? 'assessmentEN'
                          : 'assessmentFR'
                    }).href,

                  permission:
                    'ASSESSMENT.VIEW'
                }
      ]
    },

    // ========================================================
    // PROFILE
    // ========================================================
    {
      id:
        'my-profile',

      title:
        currentLanguage.value === 'en'
          ? 'Profile'
          : 'Mon profil',

      icon:
        'mdi-account-circle-outline',

      children: [
        {
          id:
            'profile-edit',

          title:
            currentLanguage.value === 'en'
              ? 'Edit profile'
              : 'Modifier le profil',

          icon:
            'mdi-account-edit-outline',

          to:
            router.resolve({
              name:
                currentLanguage.value === 'en'
                  ? 'EditProfileEN'
                  : 'EditProfileFR'
            }).href,

          permission:
            'MESSAGE.CREATE'
        },

        {
          id:
            'profile-messages',

          title:
            currentLanguage.value === 'en'
              ? 'My messages'
              : 'Mes messages',

          icon:
            'mdi-message-outline',

          to:
            router.resolve({
              name:
                currentLanguage.value === 'en'
                  ? 'MessagingEN'
                  : 'MessagingFR'
            }).href,

          permission:
            'MESSAGE.CREATE'
        }
      ]
    },

    // ========================================================
    // ASSESSMENT
    // ========================================================
    {
      id:
        'my-dashboard',

      title:
        currentLanguage.value === 'en'
          ? 'Assessment'
          : 'Évaluation',

      icon:
        'mdi-head-question-outline',

      children: [
        {
          id:
            'assessment-take',

          title:
            currentLanguage.value === 'en'
              ? 'Take assessment'
              : 'Faire une évaluation',

          icon:
            'mdi-clipboard-text-outline',

          to:
            router.resolve({
              name:
                currentLanguage.value === 'en'
                  ? 'assessmentEN'
                  : 'assessmentFR'
            }).href,

          permission:
            'ASSESSMENT.VIEW'
        }
      ]
    }
  ])

// ============================================================
// PERMISSION FILTERING
// ============================================================

function canAccessItem(
  item
) {
  if (
    !item
  ) {
    return false
  }

  if (
    item.public === true
  ) {
    return true
  }

  if (
    item.permission &&
    !auth.hasPermission(
      item.permission
    )
  ) {
    return false
  }

  return true
}

function filterByAccess(
  items
) {
  return (
    items || []
  )
    .map(
      item => {
        if (
          !canAccessItem(
            item
          )
        ) {
          return null
        }

        if (
          item.children?.length
        ) {
          const children =
            filterByAccess(
              item.children
            )

          if (
            children.length === 0
          ) {
            return item.to
              ? {
                  ...item,
                  children:
                    undefined
                }
              : null
          }

          return {
            ...item,
            children
          }
        }

        return item
      }
    )
    .filter(Boolean)
}

// ============================================================
// SEARCH / AUTHORIZATION
// ============================================================

function filterItems(
  items,
  query
) {
  const q =
    String(
      query || ''
    )
      .trim()
      .toLowerCase()

  return (
    items || []
  )
    .map(
      item => {
        if (
          !canAccessItem(
            item
          )
        ) {
          return null
        }

        const match =
          !q ||
          String(
            item.title || ''
          )
            .toLowerCase()
            .includes(q)

        if (
          item.children?.length
        ) {
          const filteredChildren =
            filterItems(
              item.children,
              q
            )

          if (
            filteredChildren.length >
            0
          ) {
            return {
              ...item,
              children:
                filteredChildren
            }
          }

          if (
            match
          ) {
            return {
              ...item,
              children:
                filterByAccess(
                  item.children
                )
            }
          }

          return null
        }

        return match
          ? item
          : null
      }
    )
    .filter(Boolean)
}

const filteredMenu =
  computed(() =>
    filterItems(
      filterByAccess(
        menu.value
      ),
      search.value
    )
  )

// ============================================================
// GROUP HELPERS
// ============================================================

function collectGroupIds(
  items,
  results = []
) {
  items.forEach(
    item => {
      if (
        item.children
      ) {
        results.push(
          item.id
        )

        collectGroupIds(
          item.children,
          results
        )
      }
    }
  )

  return results
}

function isGroupOpen(
  id
) {
  return open.value.includes(
    id
  )
}

function toggleGroup(
  id
) {
  const index =
    open.value.indexOf(
      id
    )

  if (
    index === -1
  ) {
    open.value.push(
      id
    )
  }
  else {
    open.value.splice(
      index,
      1
    )
  }
}

// ============================================================
// ACTIVE ROUTE
// ============================================================

function normalizePath(
  value
) {
  const normalized =
    String(
      value || ''
    )
      .replace(
        /\/+$/,
        ''
      )

  return normalized || '/'
}

function isActive(
  item
) {
  if (
    !item?.to
  ) {
    return false
  }

  return (
    normalizePath(
      route.path
    ) ===
    normalizePath(
      item.to
    )
  )
}

function isGroupActive(
  item
) {
  if (
    !item?.children?.length
  ) {
    return false
  }

  return item.children.some(
    child => {
      if (
        child.children
      ) {
        return isGroupActive(
          child
        )
      }

      return isActive(
        child
      )
    }
  )
}

// ============================================================
// NAVIGATION
// ============================================================

async function navigate(
  item
) {
  if (
    !item?.to
  ) {
    return
  }

  await router.push(
    item.to
  )
}

// ============================================================
// INITIAL GROUP STATE
// ============================================================

watch(
  menu,
  newMenu => {
    open.value =
      collectGroupIds(
        newMenu
      )
  },
  {
    immediate: true
  }
)

// ============================================================
// SEARCH EXPANSION
// ============================================================

watch(
  search,
  () => {
    if (
      !search.value
    ) {
      open.value =
        collectGroupIds(
          menu.value
        )

      return
    }

    open.value =
      collectGroupIds(
        filteredMenu.value
      )
  }
)
</script>

<style scoped>
/* ============================================================
   ROOT
============================================================ */

.module-sidebar {
  width: 100%;
  min-height: 100%;
  border-radius: 0 !important;
  background: #303030 !important;
  color: #dedede !important;
  overflow: visible;
}

/* ============================================================
   SEARCH
============================================================ */

.sidebar-search-wrapper {
  padding: 10px 8px;
}

.sidebar-search {
  width: 100%;
}

.sidebar-search :deep(.v-field) {
  background: #ffffff;
}

.sidebar-search :deep(.v-field__input),
.sidebar-search :deep(.v-label) {
  font-size: 12px;
}

/* ============================================================
   MENU
============================================================ */

.sidebar-menu {
  width: 100%;
}

/* ============================================================
   COMMON ROW
============================================================ */

.sidebar-item,
.sidebar-group-header,
.sidebar-child-item {
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  border: 0;
  color: #dedede;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

/* ============================================================
   ROOT / GROUP
============================================================ */

.sidebar-group-header,
.sidebar-item {
  min-height: 43px;
  padding: 0 10px;
  background: #303030;
  border-bottom:
    1px solid
    rgba(255, 255, 255, 0.16);
}

/* ============================================================
   CHILDREN
============================================================ */

.sidebar-group-children {
  width: 100%;
  background: #292929;
}

.sidebar-child-item {
  min-height: 38px;
  padding: 0 10px 0 28px;
  background: #292929;
  border-bottom:
    1px solid
    rgba(255, 255, 255, 0.08);
}

.sidebar-sub-item {
  padding-left: 45px;
  background: #252525;
}

/* ============================================================
   HOVER
============================================================ */

.sidebar-item:hover,
.sidebar-group-header:hover {
  background: #3a3a3a;
}

.sidebar-child-item:hover {
  background: #353535;
}

/* ============================================================
   ACTIVE
============================================================ */

.sidebar-item.active,
.sidebar-group-header.active,
.sidebar-child-item.active {
  background: #73c5c2;
  color: #ffffff;
}

.sidebar-item.active::after,
.sidebar-group-header.active::after,
.sidebar-child-item.active::after {
  content: "";
  position: absolute;
  top: 50%;
  right: -9px;
  width: 18px;
  height: 18px;
  background: #73c5c2;
  transform:
    translateY(-50%)
    rotate(45deg);
  z-index: 1;
}

/* ============================================================
   ICONS
============================================================ */

.sidebar-icon {
  position: relative;
  z-index: 2;
  min-width: 24px;
  margin-right: 7px;
  color: inherit;
}

.sidebar-child-icon {
  position: relative;
  z-index: 2;
  min-width: 20px;
  margin-right: 7px;
  color: inherit;
}

/* ============================================================
   LABELS
============================================================ */

.sidebar-label {
  position: relative;
  z-index: 2;
  flex: 1;
  overflow: hidden;
  font-size: 11px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-child-label {
  position: relative;
  z-index: 2;
  flex: 1;
  overflow: hidden;
  font-size: 10.5px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============================================================
   CHEVRON
============================================================ */

.group-chevron {
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  margin-left: auto;
  color: inherit;
}

/* ============================================================
   BADGE
============================================================ */

.sidebar-badge {
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  margin-left: 4px;
  padding: 1px 4px;
  border-radius: 3px;
  background: #ff7936;
  color: #ffffff;
  font-size: 7px;
  font-weight: 700;
}

/* ============================================================
   RESPONSIVE
============================================================ */

@media (max-width: 959px) {
  .module-sidebar {
    min-height: auto;
  }

  .sidebar-item.active::after,
  .sidebar-group-header.active::after,
  .sidebar-child-item.active::after {
    display: none;
  }
}
</style>